import { getOperationsFromProduct, getProductOperations } from './operations';
import { shapeTaxonomyAttributesForAPI } from './shapeAttributes';
import { getInventoryOperationValue } from './getOperationValue';
import { filterOperations } from './operations';
import { isValidNumber } from '../number';
import { isVelaStatus } from '../listings/statuses';
import { getSize } from '../iterable/getSize';
import { keepOld } from '../iterable/keepOld';
import { get } from '../iterable/get';

import { AMOUNT, ENABLED, NEW, OPERATIONS, REQUIRED, VALUE } from '../../constants/product';
import { COMBINATIONS, INFLUENCE, RETAIN } from '../../constants/variations';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { EMPTY, SEPARATOR } from '../../constants';
import { PROFILE_TYPE } from '../../constants/profiles';
import { ATTRIBUTE } from '../../constants/attributes';
import { FORM } from '../../constants/forms';
import { FEATURE } from '../../constants/features';

export function addProfileId({ operation, product }) {
  if (!product.has('profile')) return operation;

  switch (product.getIn(['profile', 'type'])) {
    case PROFILE_TYPE.AI: {
      return { ...operation, aiProfileId: parseInt(product.getIn(['profile', 'id'])) };
    }

    case PROFILE_TYPE.STANDARD: {
      return { ...operation, profileId: parseInt(product.getIn(['profile', 'id'])) };
    }

    default: {
      return operation;
    }
  }
}

export function shapeOperationsForAPI({ data = {}, operations, initialValue = [] }) {
  function reduceOperations(result, item) {
    const operation = item.toJS();

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        operation[key] = data[key];
      }
    }

    result.push(operation);
    return result;
  }

  return operations.reduce(reduceOperations, initialValue);
}

export function shapeNewProductForAPI({ product, scheduleAt, shopId, status, userFeatures }) {
  const channel = product.get('channel');

  function reduceFiles(result, item) {
    const [fileUrl, name, size] = get('file_url', 'name', 'size')(item);

    result.push({ file_url: fileUrl, name, size });

    return result;
  }

  switch (channel) {
    case EBAY: {
      const products = [AMOUNT.ZERO];
      let result = [{ products, type: OPERATIONS[channel].STATUS, value: status }];

      if (scheduleAt) {
        result.push({ products, type: OPERATIONS[channel].SCHEDULED_AT, value: scheduleAt });
      }

      if (product.get('productId') === NEW) {
        const operations = product.get('operations', EMPTY.LIST);
        result = shapeOperationsForAPI({ data: { products }, operations, initialValue: result });
      } else {
        result = result.concat(
          getOperationsFromProduct({
            operation: { products },
            operationTypes: getProductOperations({ channel, userFeatures }),
            product,
            toImmutable: false,
          })
        );
      }

      return result;
    }

    case ETSY: {
      const value = {};
      const variations = product.get(FORM.VARIATIONS);
      const taxonomyAttributes = product.getIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES], EMPTY.MAP);

      value.productState = status;
      value.productVariations = [];
      value.productSupply = product.getIn([FORM.DETAILS, ATTRIBUTE.IS_SUPPLY, VALUE]);
      value.productCreator = product.getIn([FORM.DETAILS, ATTRIBUTE.WHO_MADE, VALUE]);
      value.productCreationDate = product.getIn([FORM.DETAILS, ATTRIBUTE.WHEN_MADE, VALUE]);
      value.productShopSection = product.getIn([FORM.DETAILS, ATTRIBUTE.SECTION, VALUE], null);
      value.productAutoRenewal = product.getIn([FORM.DETAILS, ATTRIBUTE.SHOULD_AUTO_RENEW, VALUE]);
      value.productionPartnerIds = product
        .getIn([FORM.DETAILS, ATTRIBUTE.PRODUCTION_PARTNERS, VALUE], EMPTY.LIST)
        .toJS();

      value.isDigital = product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE]);
      value.productTitle = product.getIn([FORM.TITLE, VALUE]);
      value.productDescription = product.getIn([FORM.DESCRIPTION, VALUE]);
      value.productMaterials = product
        .getIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, VALUE], EMPTY.ORDERED_MAP)
        .merge(product.getIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, NEW], EMPTY.ORDERED_MAP))
        .join(SEPARATOR.COMMA);

      value.productAttributes = shapeTaxonomyAttributesForAPI({
        taxonomyAttributes,
        toImmutable: false,
      });

      if (value.isDigital) {
        value.files = product.getIn([FORM.FILES, VALUE]).reduce(reduceFiles, []);
      } else {
        value.returnPolicyId = product.getIn([FORM.SHIPPING, ATTRIBUTE.RETURN_POLICY, VALUE]);
        value.productShippingId = product.getIn([FORM.SHIPPING, ATTRIBUTE.PROFILE_ID, VALUE]);
        const productItemHeight = product.getIn([FORM.SHIPPING, ATTRIBUTE.HEIGHT, VALUE], null);
        const productItemLength = product.getIn([FORM.SHIPPING, ATTRIBUTE.LENGTH, VALUE], null);
        const productItemWeight = product.getIn([FORM.SHIPPING, ATTRIBUTE.WEIGHT, VALUE], null);
        const productItemWidth = product.getIn([FORM.SHIPPING, ATTRIBUTE.WIDTH, VALUE], null);
        value.productItemWeight = isValidNumber(productItemWeight)
          ? parseFloat(productItemWeight)
          : null;
        value.productItemHeight = isValidNumber(productItemHeight)
          ? parseFloat(productItemHeight)
          : null;
        value.productItemWidth = isValidNumber(productItemWidth)
          ? parseFloat(productItemWidth)
          : null;
        value.productItemLength = isValidNumber(productItemLength)
          ? parseFloat(productItemLength)
          : null;

        if (userFeatures.get(FEATURE.PROCESSING_PROFILES)) {
          value.readinessStateId = product.getIn([FORM.SHIPPING, ATTRIBUTE.PROCESSING_PROFILE, VALUE]);
        }
      }

      const personalization = product.get(FORM.PERSONALIZATION);
      const personalizationEnabled = personalization.get(ENABLED);
      value.isPersonalizable = personalizationEnabled;
      value.personalizationCharCountMax = personalizationEnabled
        ? personalization.getIn([ATTRIBUTE.CHAR_COUNT_MAX, VALUE])
        : null;
      value.personalizationInstructions = personalizationEnabled
        ? personalization.getIn([ATTRIBUTE.INSTRUCTIONS, VALUE])
        : null;
      value.personalizationIsRequired = personalizationEnabled
        ? personalization.get(REQUIRED)
        : false;

      value.productTaxonomy = parseInt(product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]));
      value.productTags = product
        .getIn([FORM.TAGS, VALUE], EMPTY.ORDERED_MAP)
        .mergeWith(keepOld, product.getIn([FORM.TAGS, NEW]))
        .join(SEPARATOR.COMMA);

      value.productImageFiles = getOperationsFromProduct({
        operationTypes: [OPERATIONS[ETSY].PHOTOS.CHANGE_TO],
        product,
        toImmutable: false,
      });

      value.productVideoFiles = getOperationsFromProduct({
        operationTypes: [OPERATIONS[ETSY].VIDEOS.CHANGE_TO],
        product,
        toImmutable: false,
      });

      if (!value.isDigital && getSize(variations.get(COMBINATIONS))) {
        const individualPrice = (
          !variations.getIn([RETAIN, ATTRIBUTE.PRICE]) &&
          getSize(variations.getIn([INFLUENCE, ATTRIBUTE.PRICE])) > 0
        );

        const individualQuantity = (
          !variations.getIn([RETAIN, ATTRIBUTE.QUANTITY]) &&
          getSize(variations.getIn([INFLUENCE, ATTRIBUTE.QUANTITY])) > 0
        );

        const individualSKU = (
          !variations.getIn([RETAIN, ATTRIBUTE.SKU]) &&
          getSize(variations.getIn([INFLUENCE, ATTRIBUTE.SKU])) > 0
        );

        const firstVisibleCombination = (
          (individualPrice || individualQuantity || individualSKU) &&
          variations.get(COMBINATIONS).find(get(ATTRIBUTE.VISIBILITY))
        );

        value.productPrice = individualPrice
          ? parseFloat(firstVisibleCombination.get(ATTRIBUTE.PRICE))
          : parseFloat(product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]));

        value.productQuantity = individualQuantity
          ? parseInt(firstVisibleCombination.get(ATTRIBUTE.QUANTITY))
          : parseInt(product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]));

        if (!individualSKU) {
          value.productSku = variations.getIn([RETAIN, ATTRIBUTE.SKU])
            ? null
            : product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]);
        }

        value.productVariations = getOperationsFromProduct({
          operationTypes: [OPERATIONS[ETSY].VARIATIONS],
          product,
          toImmutable: false,
        });
      } else {
        value.productPrice = parseFloat(product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]));
        value.productQuantity = parseInt(product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]));
        value.productSku = product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]);
        value.productVariations = [];
      }

      const operation = { shopId, type: 'product.create', value };

      if (scheduleAt) {
        value.scheduledAt = scheduleAt;
      }

      return [operation];
    }

    case FAIRE: {
      const products = [AMOUNT.ZERO];
      let result = [{ products, type: OPERATIONS[FAIRE].STATUS, value: status }];

      if (scheduleAt) {
        result.push({ products, type: OPERATIONS[FAIRE].SCHEDULED_AT, value: scheduleAt });
      }

      if (product.get('productId') === NEW) {
        const operations = product.get('operations', EMPTY.LIST);
        result = shapeOperationsForAPI({ data: { products }, operations, initialValue: result });

        if (operations.findIndex(filterOperations(OPERATIONS[FAIRE].DISTANCE_UNIT)) === -1) {
          result.push({
            products,
            type: OPERATIONS[FAIRE].DISTANCE_UNIT,
            value: getInventoryOperationValue({
              channel,
              type: ATTRIBUTE.DISTANCE_UNIT,
              value: product.getIn([FORM.INVENTORY, ATTRIBUTE.DISTANCE_UNIT, VALUE]),
            }),
          });
        }

        if (operations.findIndex(filterOperations(OPERATIONS[FAIRE].MASS_UNIT)) === -1) {
          result.push({
            products,
            type: OPERATIONS[FAIRE].MASS_UNIT,
            value: getInventoryOperationValue({
              channel,
              type: ATTRIBUTE.MASS_UNIT,
              value: product.getIn([FORM.INVENTORY, ATTRIBUTE.MASS_UNIT, VALUE]),
            }),
          });
        }
      } else {
        result = result.concat(
          getOperationsFromProduct({
            operation: { products },
            operationTypes: getProductOperations({ channel, userFeatures }),
            product,
            toImmutable: false,
          })
        );
      }

      return result;
    }

    case SHOPIFY: {
      const products = [AMOUNT.ZERO];
      let result = [{ products, type: OPERATIONS[channel].STATUS, value: status }];

      if (scheduleAt) {
        result.push({ products, type: OPERATIONS[channel].SCHEDULED_AT, value: scheduleAt });
      }

      if (product.get('productId') === NEW) {
        const operations = product.get('operations', EMPTY.LIST);
        result = shapeOperationsForAPI({ data: { products }, operations, initialValue: result });

        if (operations.findIndex(filterOperations(OPERATIONS[channel].VARIATIONS)) === -1) {
          result.push({
            products,
            type: OPERATIONS[channel].VARIATIONS,
            value: getInventoryOperationValue({ channel, inventory: product.get(FORM.INVENTORY), toImmutable: false }),
          });
        }
      } else {
        result = result.concat(
          getOperationsFromProduct({
            operation: { products },
            operationTypes: getProductOperations({ channel, userFeatures }),
            product,
            toImmutable: false,
          })
        );
      }

      return result;
    }

    default: {
      return [];
    }
  }
}

export function shapeNewLinkedProductsForAPI({
  products,
  scheduleAt,
  shopId: currentShopId,
  statuses,
  userShops,
  userFeatures,
}) {
  function reduceProducts(result, product, shopId) {
    const shop = userShops.getIn(['byId', shopId]);
    const channel = shop.get('channel');
    const db = shop.get('db');

    if (!Array.isArray(result[channel])) {
      result[channel] = [];
    }

    function getOperation() {
      const status = statuses.get(shopId, statuses.get(currentShopId));
      const operations = shapeNewProductForAPI({ product, scheduleAt, shopId, status, userFeatures });

      switch (channel) {
        case FAIRE:
        case EBAY: {
          return { db, operations, saveOnly: isVelaStatus({ channel, status }), shopId };
        }

        case ETSY: {
          return { ...operations[0], db };
        }

        case SHOPIFY: {
          return { db, operations, shopId };
        }

        default: {
          return undefined;
        }
      }
    }

    const operation = addProfileId({ operation: getOperation(), product });

    result[channel].push(operation);

    return result;
  }

  const payload = {
    newProducts: products.reduce(reduceProducts, {}),
  };

  return payload;
}
